import {
  actionFromJSON,
  parseFrame,
  FrameVal,
  MixedMediaObjectType,
} from "./FlowObject";
import Color from "./Color";
import {
  getCurrentDate,
  guidGenerator,
} from "../features/utils/commonFunctions";
import GalleryAsset from "./GalleryAsset";
import ImageAsset, { PdfAsset } from "./Assets";
import Action from "./Action";
import { Interpreter } from "xstate";
import {
  FlowObjects,
  ChildMachineSchema,
  ChildEvents,
  EventKind,
  Kind,
  IndexName,
} from "../features/fsm/types";
import { UploadResponse } from "./Screens";
import { GradientDef } from "../ai/styles/themeDecorations";
import { getShapeDefinition } from "./ShapeDefinitions";

export enum ShapeType {
  RECTANGLE = "rectangle",
  ELLIPSE = "ellipse",
  ROUNDED_RECT = "roundedRectangle",
  RIGHT_TRIANGLE = "rightTriangle",
  ISOSCELES_TRIANGLE = "isoscelesTriangle",
  STANDARD_ARROW = "standardArrow",
  OCTAGON = "octagon",
  FORTY_FIVE_ARROW = "45DegreeArrow",
  DIAMOND = "diamond",
  PENTAGON = "pentagon",
  HEXAGON = "hexagon",
  TRAPEZOID = "trapezoid",
  PARALLELOGRAM = "parallelogram",
  EQUILATERAL_TRIANGLE = "equilateralTriangle",
  DOUBLE_ARROW = "doubleArrow",
  CHEVRON = "chevron",
  CURVED_ARROW = "curvedArrow",
  STAR_5 = "star5",
  STAR_6 = "star6",
  CROSS = "cross",
  BADGE = "badge",
  HEART = "heart",
  SPEECH_BUBBLE = "speechBubble",
  THOUGHT_BUBBLE = "thoughtBubble",
  CLOUD = "cloud",
  LIGHTNING = "lightning",
  CRESCENT = "crescent",
  TEAR_DROP = "tearDrop",
  HALF_CIRCLE = "halfCircle",
  QUARTER_CIRCLE = "quarterCircle",
  RING = "ring",
}

export default class ShapeFlowObject implements Kind {
  id: string;
  borderColor: Color;
  rotation: 0 | 1 | 2 | 3;
  galleryAssets: GalleryAsset | null;
  updated: string;
  frame: FrameVal;
  videoAsset: ImageAsset | null;
  zoomScale: number;
  retinaImageAsset: ImageAsset | null;
  pdfAsset: PdfAsset | null;
  type: MixedMediaObjectType;
  action: Action | null;
  originalImageAsset: ImageAsset | null;
  borderWidth: number;
  zoomOffset: {
    height: number;
    width: number;
  };
  audioAsset: ImageAsset | null;
  croppedImageAsset: ImageAsset | null;
  created: string;
  locked: boolean;
  /**
   * Hidden from JSON.parse, this informs video/pdf objects to only use previewImage
   */
  __forceUseImage: boolean;
  /**
   * Hidden from JSON.parse, marks this shape as an AI image placeholder awaiting generation
   */
  __isImagePlaceholder: boolean;
  /**
   * Associated {@link MediaMachine} for this object
   */
  __ref?: Interpreter<
    { mmo: FlowObjects },
    ChildMachineSchema,
    ChildEvents,
    any
  >;
  color: Color;
  shapeType: ShapeType;
  cornerRadius: number;
  pointsArrayString: string;
  /** Optional gradient fill, when set, renders as SVG gradient instead of solid color */
  gradientDef?: GradientDef;

  constructor(
    id: string,
    borderColor: Color,
    rotation: 0 | 1 | 2 | 3,
    galleryAssets: GalleryAsset | null,
    updated: string,
    frame: FrameVal,
    videoAsset: ImageAsset | null,
    zoomScale: number,
    retinaImageAsset: ImageAsset | null,
    pdfAsset: PdfAsset | null,
    type: MixedMediaObjectType,
    action: Action | null,
    originalImageAsset: ImageAsset | null,
    borderWidth: number,
    zoomOffset: {
      height: number;
      width: number;
    },
    audioAsset: ImageAsset | null,
    croppedImageAsset: ImageAsset | null,
    created: string,
    locked: boolean,
    color: Color,
    shapeType: ShapeType,
    pointsArrayString: string,
    cornerRadius: number = 0
  ) {
    this.id = id;
    this.borderColor = borderColor;
    this.rotation = rotation;
    this.galleryAssets = galleryAssets;
    this.updated = updated;
    this.frame = frame;
    this.videoAsset = videoAsset;
    this.zoomScale = zoomScale;
    this.retinaImageAsset = retinaImageAsset;
    this.pdfAsset = pdfAsset;
    this.type = type;
    this.action = action;
    this.originalImageAsset = originalImageAsset;
    this.borderWidth = borderWidth;
    this.zoomOffset = zoomOffset;
    this.audioAsset = audioAsset;
    this.croppedImageAsset = croppedImageAsset;
    this.created = created;
    this.locked = locked;
    this.__forceUseImage = false;
    this.__isImagePlaceholder = false;
    this.color = color;
    this.shapeType = shapeType;
    this.pointsArrayString = pointsArrayString;
    this.cornerRadius = cornerRadius;
  }

  eventKind(): EventKind {
    return EventKind.Screen;
  }

  static fromJSON(json: any): ShapeFlowObject {
    let flowObj = Object.create(ShapeFlowObject.prototype);
    return Object.assign(flowObj, json, {
      color: Color.fromJSON(json.color),
      borderColor: Color.fromJSON(json.borderColor),
      action: actionFromJSON(json.action),
      frame: parseFrame(json.frame),
      cornerRadius: json.cornerRadius || 0,
    });
  }

  static reviver(key: string, value: any): any {
    return key === "" ? ShapeFlowObject.fromJSON(value) : value;
  }

  static derivePointsArray(kind: string): [ShapeType, string] {
    const def = getShapeDefinition(kind);
    if (def) {
      return [(def.kind as ShapeType), def.pointsArrayString];
    }
    return [ShapeType.RECTANGLE, ""];
  }

  static default(kind: string): ShapeFlowObject {
    const [kindAsType, pointsArrayString] = this.derivePointsArray(kind);
    return new ShapeFlowObject(
      guidGenerator(),
      Color.iosToCssColor("00000"),
      0,
      GalleryAsset.default(),
      getCurrentDate(),
      {
        height: 150.0,
        width: 150.0,
        x: 250.0,
        y: 250.0,
      },
      null,
      1,
      null,
      null,
      "shape",
      null,
      null,
      0,
      {
        height: 0,
        width: 0,
      },
      null,
      null,
      getCurrentDate(),
      false,
      Color.iosToCssColor("1F70A8CC"),
      kindAsType,
      pointsArrayString
    );
  }

  changeColor(color: Color): void {
    this.color = color;
  }

  setMMOUrl(response: UploadResponse[], indexMap: Map<IndexName, number>) {
    console.log("noop");
  }

  pointsAsArray(): [number, number][] {
    const asArray: [number, number][] = this.pointsArrayString
      .split(" ")
      .map((val) => {
        let [z, o] = val.split(",");
        return [parseFloat(z), parseFloat(o)];
      });
    return asArray;
  }

  pointArray(scale: PointScale): number[] {
    let arr: number[] = [];
    this.pointsArrayString.split(" ").forEach((val) => {
      let [z, o] = val.split(",");
      arr.push(parseFloat(z) * scale.width);
      arr.push(parseFloat(o) * scale.height);
    });
    return arr;
  }

  rotate(times: 0 | 1 | 2 | 3) {
    console.info("old string", this.pointsArrayString);
    const rotationTheta = times * -1 * (Math.PI / 2);
    const [centerX, centerY] = [0.5, 0.5];
    let newPoints = this.pointsAsArray().map(([x, y]) => {
      let shiftedX = x - centerX;
      let shiftedY = y - centerY;
      console.info("prev", x, y, shiftedX, shiftedY, rotationTheta);
      const angle = times * 90;
      var rad = angle * (Math.PI / 180.0);
      var xTick =
        Math.cos(rad) * (x - centerX) - Math.sin(rad) * (y - centerY) + centerX;
      var yTick =
        Math.sin(rad) * (x - centerX) + Math.cos(rad) * (y - centerY) + centerY;
      console.info("new point", [xTick, yTick]);
      return [xTick, yTick];
    });
    let newPointArray = "";
    for (let index = 0; index < newPoints.length; index++) {
      const [x, y] = newPoints[index];
      newPointArray += `${x.toFixed(5)},${y.toFixed(5)}`;
      if (index < newPoints.length - 1) {
        newPointArray += " ";
      }
    }
    console.info("new string", newPointArray);
    //this.pointsArrayString = newPointArray;
  }
}

type PointScale = { width: number; height: number };
